// The accessible name of a sortable column header.
//
// kore's DataTable wraps a sortable header's label in a `<button>`, so the
// button's accessible name is exactly the column label — which is also the
// label kore's SearchBar gives that facet's filter trigger. On the screens
// table both "Location" and "Offset" are a column and a filter, so a screen-reader
// user hears two identically named buttons that do entirely different things,
// with nothing in the name to tell them apart. (The screens list test used to
// scope its picker lookups to `role="dialog"` to work around the same
// collision, which is how it was noticed.)
//
// kore's `DataTableColumn` has no `aria-label` and the button is not ours to
// render, but `header` is a ReactNode — and the accessible-name computation
// consults a descendant's `aria-label` when naming an element from its
// contents, so wrapping the label is enough. The visible text is unchanged and
// so is `textContent`; only the announced name differs. The alternative,
// visually-hidden text beside an `aria-hidden` label, duplicates the string
// into the DOM for every header.

import type { ReactNode } from "react";

/** `label` for a plain header, "Sort by <label>" for one that sorts. */
export function sortableHeader(label: string, sortable: boolean): ReactNode {
    if (!sortable) return label;
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: the rule is about this span's OWN name, which a generic element does not expose — that is not what this is for. It is read one level up, by the name-from-content pass over kore's sort <button>, which is where the label collides with the filter trigger of the same name. tests/routes/screens-list.test.tsx pins the resulting name, but that only proves dom-accessibility-api honours it; browsers consulting a descendant's aria-label during name-from-content is established practice rather than a guarantee, so this wants one manual check with a real screen reader.
    return <span aria-label={`Sort by ${label.toLowerCase()}`}>{label}</span>;
}
